import { LocalizationContextBuilder, MessageBundle, LocalizationContext } from '@amzn/arb-tools';
import translations from 'translations';
import arbManifest from 'translations/arbManifest';

import { DEFAULT_LOCALE, ErrorConstants } from './constants';
import { logger } from './logger';

const LOCALIZATION_CONTEXTS = new Map<string, LocalizationContext>();

export function getLocalizationContext(locale: string) {
  let lc = LOCALIZATION_CONTEXTS.get(locale);

  if (!lc) {
    lc = new LocalizationContextBuilder()
      .withLocale(locale)
      .withDefaultLocale(DEFAULT_LOCALE)
      .build();

    LOCALIZATION_CONTEXTS.set(locale, lc);
  }

  return lc;
}

export function t(bundle: MessageBundle, stringId: string, args?: Record<string, unknown>) {
  if (!bundle || !stringId) {
    return undefined;
  }

  if (!args) {
    return bundle.getMessage(stringId);
  }

  return bundle.formatMessage(stringId, args);
}

export function resolveBundleUrlWithFallbacks(
  resourcePackPath: string | undefined,
  locale: string,
  bundle: string,
) {
  if (resourcePackPath && translations[resourcePackPath]) {
    return translations[resourcePackPath];
  }

  if (!resourcePackPath && arbManifest.endpointMap?.[locale]?.[bundle]) {
    return translations[arbManifest.endpointMap[locale][bundle]];
  }

  if (arbManifest.endpointMap?.[DEFAULT_LOCALE]?.[bundle]) {
    return translations[arbManifest.endpointMap[DEFAULT_LOCALE][bundle]];
  }
}

/**
 * This is an exhaustive type check pattern in TypeScript that ensures all possible values of 
 * the confirmationContext enum are handled in the switch statement. If a new value is added 
 * to the enum but not handled in the switch, TypeScript will show a compile error. The never 
 * type is used because it should be impossible to reach this case if all enum values are 
 * handled, helping catch missing cases at compile time rather than runtime.
 * 
 * Ref: https://www.geeksforgeeks.org/typescript-exhaustiveness-checking/
 
* @param value - Value that should be of type never
* @returns The value cast as never (should be unreachable)
*/
export const exhaustiveCheck = (value: never): never => value;

/**
 * Returns the first value from a list if the list contains exactly one item
 * @param list - Array to check for a single item
 * @returns The first item if list length is 1, otherwise undefined
 */
export function getFirstValueIfSingleItem<T>(list: Array<T>): T | undefined {
  return list.length === 1 ? list[0] : undefined;
}

/**
 * Fetches metadata from a URL using HEAD request
 * @param {string} url - URL to fetch metadata from
 * @returns {Promise<Headers>}
 * @throws {Error} When HEAD request fails
 * @private
 */
export async function fetchUrlMetadata(url: string, timeoutInMS = 5000): Promise<Headers> {
  const urlObject = new URL(url);
  const headResponse: Response = await fetch(urlObject, {
    method: 'HEAD',
    signal: AbortSignal.timeout(timeoutInMS),
  });
  if (!headResponse.ok) {
    throw new Error(
      `${ErrorConstants.MESSAGES.HTTP_ERROR}: { status: ${headResponse.status} - ${headResponse.statusText} }`,
    );
  }
  logger.debug('Headers:', { headers: headResponse.headers });
  return headResponse.headers;
}
